import { debounceStorage } from "../../../../..";
import { ui, temp, state } from "../../../../../interfaces/interfaces";
import { validateSideTab } from "@utils/general";
import { serviceAgreement } from "@utils/objects/serviceAgreement_v2";
import { sanitize, validateAddress, validateBoiler, validateOffer, validatePlanOptions, validateProperty } from "@utils/validation";

const FORM_VALIDATION_DELAY = 150;
const CarePlanPrefix = "CarePlanPlanDetails";
const PlanOptionsPrefix = `${CarePlanPrefix}PlanOptions`;
const OfferPrefix = `${CarePlanPrefix}Offer`;
const AddressPrefix = `${CarePlanPrefix}Address`;
const PropertyPrefix = `${CarePlanPrefix}Property`;

const handleAddressAsProperty = (action: { payload: { name: string; value: any }; }, next: any) => {
    const Address = serviceAgreement.serviceAgreementPolicyAddress;
    if (action.payload.value) {
        // Logic for when the address is to be set as a property address
        Address.property = ui.PropertyAddressProperty;
        Address.street = ui.PropertyAddressStreet;
        Address.locality = ui.PropertyAddressLocality;
        Address.town = ui.PropertyAddressCity;
        Address.postcode = ui.PropertyAddressPostcode;
        Address.organisation = ui.PropertyAddressBusinessName;
    } else {
        // Logic for the default case
        Address.property = ui.CarePlanPlanDetailsAddressProperty;
        Address.street = ui.CarePlanPlanDetailsAddressStreet;
        Address.locality = ui.CarePlanPlanDetailsAddressLocality;
        Address.town = ui.CarePlanPlanDetailsAddressCity;
        Address.postcode = ui.CarePlanPlanDetailsAddressPostcode;
        Address.organisation = "";
    }
    {
        const validateInputAndForm = () => {
            validateSideTab(AddressPrefix as "CarePlanPlanDetailsAddress");
        }
        debounceStorage.addOrExecute({ name: action.payload.name, delay: FORM_VALIDATION_DELAY, callback: validateInputAndForm });
    }
    return next(action);
}


const handlePropertyInput = (action: { payload: { name: string; value: any }; }, next: any) => {
    const property = action.payload.name.replace(CarePlanPrefix, "");
    const subStep = property.includes("Address") ? "Address" : "Property";
    const Address = serviceAgreement.serviceAgreementPolicyAddress;

    const sanitizeProperty = property.replace(subStep, "");
    action.payload.value = sanitize(action.payload.value, sanitizeProperty, false);

    const validateInputAndForm = () => {
        const validateFn = subStep === "Address" ? validateAddress : validateProperty;
        validateFn(`${CarePlanPrefix}${subStep}` as never, sanitizeProperty as never, action.payload.value);
        validateSideTab(`${CarePlanPrefix}${subStep}`);
    }
    debounceStorage.addOrExecute({ name: action.payload.name, delay: FORM_VALIDATION_DELAY, callback: validateInputAndForm });

    switch (sanitizeProperty) {
        case "Property":
            Address.property = action.payload.value;
            break;
        case "BusinessName":
            Address.organisation = action.payload.value;
            break;
        case "Street":
            Address.street = action.payload.value;
            break;
        case "Locality":
            Address.locality = action.payload.value;
            break;
        case "City":
            Address.town = action.payload.value;
            break;
        case "Postcode":
            Address.postcode = action.payload.value;
            break;
        case "BuildingTypes":
            Address.propertyBuildingTypeId = action.payload.value?.propertyBuildingTypeId;
            break;
        case "Bathrooms":
            Address.bathrooms = action.payload.value;
            break;
        case "Bedrooms":
            Address.bedrooms = action.payload.value;
            break;
        case "ExternalDoors":
            Address.externalDoors = action.payload.value;
            break;
        case "GasAppliances":
            Address.gasAppliances = action.payload.value;
            break;
        case "Radiators":
            Address.radiators = action.payload.value;
            break;
        case "Comments":
            serviceAgreement.serviceAgreement.notes = action.payload.value;
            break;
    }

    return next(action);
}

const handleServiceAgreementInput = (action: { payload: { name: string; value: any }; }) => {
    switch (true) {
        case action.payload.name.includes("PlanType"):
            serviceAgreement.serviceAgreement.serviceAgreementTypeId = action.payload.value?.serviceAgreementTypeId ? action.payload.value.serviceAgreementTypeId : 0;
            serviceAgreement.serviceAgreementOffer.serviceAgreementOfferId = 0;
            serviceAgreement.serviceAgreementOffer.serviceAgreementOfferAllocationReasonId = 0;
            break;

        case action.payload.name.includes("Duration"):
            serviceAgreement.serviceAgreementOffer.serviceAgreementDurationId = action.payload.value?.serviceAgreementDurationId ? action.payload.value.serviceAgreementDurationId : 0;
            break;

        case action.payload.name.includes("BillingFrequency"):
            serviceAgreement.serviceAgreementPaymentDetails.serviceAgreementBillingFrequencyId = action.payload.value?.serviceAgreementBillingFrequencyId ? action.payload.value.serviceAgreementBillingFrequencyId : 0;
            break;

        case action.payload.name.includes("OfferType"):
            serviceAgreement.serviceAgreementOffer.serviceAgreementOfferId = action.payload.value?.serviceAgreementOfferId ? action.payload.value.serviceAgreementOfferId : 0;
            serviceAgreement.serviceAgreementOffer.serviceAgreementOfferAllocationReasonId = 0;
            break;

        case action.payload.name.includes("OfferReason"):
            serviceAgreement.serviceAgreementOffer.serviceAgreementOfferAllocationReasonId = action.payload.value?.serviceAgreementOfferAllocationReasonId ? action.payload.value.serviceAgreementOfferAllocationReasonId : 0;
            break;

        case action.payload.name.includes("PromoCode"):
            const campaign = temp.AvailablePromoCodes.find((x) => x.campaignCode === action.payload.value);
            if (campaign) {
                serviceAgreement.serviceAgreementOffer.serviceAgreementCampaignCodeId = campaign.serviceAgreementCampaignCodeId || null;
            }
            else {
                serviceAgreement.serviceAgreementOffer.serviceAgreementCampaignCodeId = null;
            }
            break;

        case action.payload.name.includes("Excess"):
            break;

        default:
            // Handle any other cases or log an unexpected case
            console.warn("Unhandled case in handleServiceAgreementInput:", action.payload.name);
            break;
    }
}


const handlePlanOptionsInput = (action: { payload: { name: string; value: any }; }, next: any) => {
    if (action.payload.name === "CarePlanPlanDetailsPlanOptionsPlanType") {
        ui.CarePlanPlanDetailsOfferOfferType = undefined;
        ui.CarePlanPlanDetailsOfferOfferReason = undefined;
        temp.AvailableOffers = [];

        // Boiler OR Landlord should be presented?
        if (action.payload.value?.serviceAgreementTypeId) {
            if (action.payload.value.baseTradePlan.toUpperCase().includes("HE")) {
                ui.CarePlanPlanDetailsIsBoiler = true;
            } else ui.CarePlanPlanDetailsIsBoiler = false;

            if (action.payload.value.name.toUpperCase().includes("LANDLORD")) {
                ui.CarePlanPlanDetailsIsLandlord = true;
            } else ui.CarePlanPlanDetailsIsLandlord = false;
        } else {
            ui.CarePlanPlanDetailsIsBoiler = false;
            ui.CarePlanPlanDetailsIsLandlord = false;
        }
    }

    const extractedProperty = action.payload.name.replace(PlanOptionsPrefix, "");
    const validateInputAndForm = () => {
        validatePlanOptions(
            PlanOptionsPrefix as "CarePlanPlanDetailsPlanOptions",
            extractedProperty as "Duration" | "BillingFrequency" | "Excess" | "PlanType",
            action.payload.value
        );
        validateSideTab(PlanOptionsPrefix as "CarePlanPlanDetailsPlanOptions");
    }
    debounceStorage.addOrExecute({ name: action.payload.name, delay: FORM_VALIDATION_DELAY, callback: validateInputAndForm });

    handleServiceAgreementInput(action);

    return next(action);
}

const handleOfferInput = (action: { payload: { name: string; value: any }; }, next: any) => {
    const Offer = serviceAgreement.serviceAgreementOffer;
    let subType = "";

    switch (true) {
        case action.payload.name.includes("OfferType"):
            subType = "OfferType";
            Offer.serviceAgreementOfferId = action.payload.value?.serviceAgreementOfferId
                ? action.payload.value.serviceAgreementOfferId
                : 0;
            !state.PromoCodeValid && (ui.CarePlanPlanDetailsOfferOfferReason = undefined);
            !state.PromoCodeValid && (Offer.serviceAgreementOfferAllocationReasonId = 0);
            break;

        case action.payload.name.includes("OfferReason"):
            subType = "OfferReason";
            Offer.serviceAgreementOfferAllocationReasonId = action.payload.value?.serviceAgreementOfferAllocationReasonId
                ? action.payload.value.serviceAgreementOfferAllocationReasonId
                : 0;
            break;
        case action.payload.name.includes("PromoCode"):
            subType = "PromoCode";
            action.payload.value = sanitize(action.payload.value, "PromoCode", false);
            if (action.payload.value && action.payload.value.length > 0) {
                Offer.serviceAgreementOfferId = 0;
                Offer.serviceAgreementOfferAllocationReasonId = 0;
                ui.CarePlanPlanDetailsOfferOfferType = undefined;
                ui.CarePlanPlanDetailsOfferOfferReason = undefined;
            }
            break;

        default:
            // Handle any other cases or log an unexpected case
            console.warn("Unhandled case in handleOfferInput:", action.payload.name);
            break;
    }

    const validateInputAndForm = () => {
        validateOffer(OfferPrefix as "CarePlanPlanDetailsOffer", subType as "OfferType" | "OfferReason" | "PromoCode", action.payload.value);
        validateSideTab(OfferPrefix as "CarePlanPlanDetailsOffer");
    }
    debounceStorage.addOrExecute({ name: action.payload.name, delay: FORM_VALIDATION_DELAY, callback: validateInputAndForm });

    handleServiceAgreementInput(action);

    return next(action);
}

const handleBoilerInput = (action: { payload: { name: string; value: any }; }, next: any) => {
    const SA = serviceAgreement.serviceAgreement;
    switch (action.payload.name) {
        case "CarePlanPlanDetailsBoilerHeatingTypes":
            SA.boilerHeatingTypeId = action.payload.value?.boilerHeatingTypeId ? action.payload.value.boilerHeatingTypeId : 0;
            break;

        case "CarePlanPlanDetailsBoilerBoilerManufacturers":
            SA.boilerManufacturerId = action.payload.value?.boilerManufacturerId ? action.payload.value.boilerManufacturerId : 0;
            break;

        case "CarePlanPlanDetailsBoilerBoilerModel":
            SA.boilerModel = action.payload.value;
            break;

        case "CarePlanPlanDetailsBoilerBoilerInstallationDate":
            SA.dateBoilerInstalled = new Date(action.payload.value);
            break;

        case "CarePlanPlanDetailsBoilerGasSafetyDueDate":
            SA.dateGasSafetyDue = new Date(action.payload.value);
            break;

        case "CarePlanPlanDetailsBoilerBoilerLastServicedDate":
            SA.dateBoilerLastServiced = new Date(action.payload.value);
            break;

        default:
            // Handle any other cases or log an unexpected case
            console.warn("Unhandled case in handleBoilerInput:", action.payload.name);
            break;
    }

    const validateInputAndForm = () => {
        const extractedProperty = action.payload.name.replace(CarePlanPrefix, "").replace("BoilerBoiler", "Boiler");
        validateBoiler("CarePlanPlanDetailsBoiler", extractedProperty as "BoilerModel" | "BoilerManufacturers" | "BoilerInstallationDate" | "BoilerLastServicedDate", action.payload.value);
        validateSideTab("CarePlanPlanDetailsBoiler");
    }
    debounceStorage.addOrExecute({ name: action.payload.name, delay: FORM_VALIDATION_DELAY, callback: validateInputAndForm });

    return next(action);
}

export const planDetailsMiddleware = async (action: { payload: { name: string; value: any } }, next: any) => {
    switch (action.payload.name) {
        // Property related cases
        case `${AddressPrefix}Property`:
        case `${AddressPrefix}City`:
        case `${AddressPrefix}BusinessName`:
        case `${AddressPrefix}Locality`:
        case `${AddressPrefix}Postcode`:
        case `${AddressPrefix}Street`:
        case `${PropertyPrefix}Bathrooms`:
        case `${PropertyPrefix}Bedrooms`:
        case `${PropertyPrefix}BuildingTypes`:
        case `${PropertyPrefix}ExternalDoors`:
        case `${PropertyPrefix}GasAppliances`:
        case `${PropertyPrefix}Radiators`:
            return handlePropertyInput(action, next);

        case `${PropertyPrefix}ExclusionDetails`:
            {
                const SA = serviceAgreement.serviceAgreement;
                SA.exclusionDetails = action.payload.value;
                return next(action);
            }
        // Plan Options related cases
        case `${PlanOptionsPrefix}Comments`:
            {
                const SA = serviceAgreement.serviceAgreement;
                SA.notes = action.payload.value;
                return next(action);
            }
        case `${PlanOptionsPrefix}PlanType`:
        case `${PlanOptionsPrefix}Duration`:
        case `${PlanOptionsPrefix}BillingFrequency`:
        case `${PlanOptionsPrefix}Excess`:
            return handlePlanOptionsInput(action, next);

        // Offer related cases
        case `${OfferPrefix}OfferReason`:
        case `${OfferPrefix}OfferType`:
        case `${OfferPrefix}PromoCode`:
            return handleOfferInput(action, next);

        // Boiler related cases
        case `${CarePlanPrefix}BoilerBoilerInstallationDate`:
        case `${CarePlanPrefix}BoilerBoilerLastServicedDate`:
        case `${CarePlanPrefix}BoilerBoilerManufacturers`:
        case `${CarePlanPrefix}BoilerBoilerModel`:
        case `${CarePlanPrefix}BoilerGasSafetyDueDate`:
        case `${CarePlanPrefix}BoilerHeatingTypes`:
            return handleBoilerInput(action, next);

        // Address related cases
        case `${AddressPrefix}AsProperty`:
            return handleAddressAsProperty(action, next);

        default:
            return next(action);
    }
};